import {
  Box,
  Drawer,
  Group,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconLogout, IconUser } from "@tabler/icons-react";
import type { ReactNode } from "react";
import type { Module } from "../lib/api";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { BottomNav, ModuleRail } from "../components/ui";
import {
  MODULE_META,
  HOME_ENTRY,
  activeNavEntry,
  moduleAccent,
  navGroups,
  type NavGroup,
} from "./modules";
import { useMyWork } from "./MyWorkContext";

/**
 * The chrome around every signed-in screen: the phone's bottom nav, the
 * desktop's module rail, and nothing else.
 *
 * It deliberately does NOT own the page header. Screens migrate to the new
 * design one phase at a time, so for as long as some of them still draw their
 * own `.page__header` this shell drawing one too would give them two. The
 * header lives in `Page`, which a screen adopts when its phase comes.
 *
 * Navigation is global from Phase 2 regardless, because that is the part a
 * half-migrated app cannot fake: a person should not lose their tabs by
 * tapping into a screen nobody has got to yet.
 *
 * Both navs are fed the modules the SESSION holds. Every route is
 * independently guarded and every endpoint enforces access on its own, so
 * this is usability rather than security - but offering somebody a tab that
 * bounces them is still a bug, and there is a browser-drive check that says
 * so.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { user, modules, signOut, hasRole } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [moreOpen, more] = useDisclosure(false);
  // The badges. Zero when the count request has not landed or has failed,
  // which both navs render as no badge at all - an empty badge would read as
  // "nothing waiting", and that is a different claim from "not known yet".
  const { countFor, toneFor } = useMyWork();

  // Store management moved off Home and into the nav, so the nav now has to
  // know who owns the master lists. Same two roles the screens are gated to.
  const groups = navGroups(modules, hasRole("STORE_MANAGER", "ADMIN"));
  const active = activeNavEntry(pathname, groups);

  const go = (key: string) => {
    more.close();
    if (key === HOME_ENTRY.key) {
      navigate(HOME_ENTRY.path);
      return;
    }
    // Looked up in the GROUPS rather than in MODULE_META: the nav now carries
    // plain links - Products, Price list, Reports - that are not modules and
    // have no entry there. It used to cast the key to a Module and silently
    // do nothing for anything else.
    const entry = groups.flatMap((group) => group.items).find((item) => item.key === key);
    if (entry) navigate(entry.path);
  };

  /**
   * The phone's tabs: Home, then the FIELD modules this person holds.
   *
   * Store management and Admin are not offered a tab. They are the desk's
   * work, done sitting down, and a phone has five slots - spending one on
   * Products would cost a field module its place. They live in More, grouped
   * exactly as the rail groups them.
   */
  const fieldGroup = groups.find((group) => group.key === "field");
  const navEntries: {
    key: string;
    label: string;
    icon: ReactNode;
    count: number;
    accent?: string;
  }[] = [
    // Home carries no badge: it is the screen every count is already ON, so a
    // dot there would only point at itself.
    {
      key: HOME_ENTRY.key,
      label: HOME_ENTRY.shortLabel,
      icon: HOME_ENTRY.icon,
      count: 0,
    },
    ...(fieldGroup?.items ?? []).map((item) => ({
      key: item.key,
      label: item.module ? MODULE_META[item.module].shortLabel : item.label,
      icon: item.icon,
      // A dot on the phone and a number on the desktop, from one figure. The
      // phone has 58px of width for an icon and a label; a count in there
      // would either truncate the label or shrink it below legible.
      count: item.module ? countFor(item.module) : 0,
      accent: item.module ? moduleAccent(item.module).fg : undefined,
    })),
  ];

  /** What did not fit on the phone, still in its groups. */
  const overflow: NavGroup[] = groups
    .map((group) =>
      group.key === "field"
        ? { ...group, items: group.items.slice(Math.max(0, 5 - 1)) }
        : group,
    )
    .filter((group) => group.items.length > 0);

  return (
    <Box style={{ minHeight: "100dvh", display: "flex", flexDirection: "column" }}>
      <Box style={{ flex: 1, display: "flex", minHeight: 0 }}>
        {/* Desktop: the rail. Icon-only below lg, per the brief - an icon
            alone is a guess unless you already know what it means, so the
            label goes only when there is genuinely no room for it. */}
        <Box visibleFrom="md" style={{ position: "sticky", top: 0, height: "100dvh" }}>
          <RailFor
            groups={groups}
            countFor={countFor}
            toneFor={toneFor}
            active={active}
            onSelect={go}
            userName={user?.name ?? ""}
            onProfile={() => navigate("/profile")}
            onSignOut={signOut}
          />
        </Box>

        <Box style={{ flex: 1, minWidth: 0 }}>{children}</Box>
      </Box>

      {/* Mobile: the bottom nav, sticky, with the safe-area inset under it. */}
      <Box hiddenFrom="md">
        <BottomNav
          items={navEntries.map((entry) => ({
            ...entry,
            alert: entry.count > 0,
            alertColor: entry.accent,
          }))}
          active={active}
          onSelect={go}
          /* ALWAYS offered, even when no navigation group overflowed.

             It was `needsMore ? more.open : undefined`, and `needsMore`
             counted navigation groups only. But the drawer also carries the
             "You" group - Profile and Sign out - which on a phone is the
             ONLY place either of them exists: the desktop rail has a footer,
             the bottom nav has no room for one. So a person whose nav
             happened to fit, a delivery agent with one field module and
             neither a store nor an admin group, had no way to sign out at
             all.

             Reported from a phone. The reason no drive caught it is that
             every browser drive signs in as an Admin, for whom the store and
             admin groups make `needsMore` true - the bug was invisible to
             the only account being tested. There is now a check that opens
             the phone nav as a field-only account and looks for Sign out. */
          onMore={more.open}
        />
      </Box>

      <Drawer
        opened={moreOpen}
        onClose={more.close}
        position="bottom"
        /* "More", matching the tab that opens it. It said "Everything else",
           which was true while the drawer only ever held what did not fit -
           but it now always holds Profile and Sign out, and for a field agent
           with nothing overflowing those two ARE the drawer. "Everything
           else" than what, in that case. */
        title="More"
        size="auto"
        radius="md"
      >
        <Stack gap="xs" pb="md">
          {/* The same groups the rail draws, so the two navs do not disagree
              about what Store management is. */}
          {overflow.map((group) => (
            <Stack key={group.key} gap={2}>
              <Text
                fz={11}
                fw={700}
                c="gray.6"
                tt="uppercase"
                px="sm"
                pt="xs"
                style={{ letterSpacing: ".06em" }}
              >
                {group.label}
              </Text>
              {group.items.map((item) => (
                <MoreRow
                  key={item.key}
                  icon={item.icon}
                  label={item.label}
                  onClick={() => go(item.key)}
                />
              ))}
            </Stack>
          ))}
          <Text
            fz={11}
            fw={700}
            c="gray.6"
            tt="uppercase"
            px="sm"
            pt="xs"
            style={{ letterSpacing: ".06em" }}
          >
            You
          </Text>
          <MoreRow
            icon={<IconUser size={21} stroke={1.6} />}
            label="Profile"
            onClick={() => {
              more.close();
              navigate("/profile");
            }}
          />
          <MoreRow
            icon={<IconLogout size={21} stroke={1.6} />}
            label="Sign out"
            onClick={signOut}
          />
        </Stack>
      </Drawer>
    </Box>
  );
}

/**
 * Split out only so the collapsed breakpoint is expressed once. Two Boxes
 * with visibleFrom/hiddenFrom rather than useMediaQuery, because a hook that
 * measures the window renders the wrong nav for one frame on first paint.
 */
function RailFor(props: {
  groups: NavGroup[];
  countFor: (module: Module) => number;
  toneFor: (module: Module) => "neutral" | "waiting" | "alert";
  active: string;
  onSelect: (key: string) => void;
  userName: string;
  onProfile: () => void;
  onSignOut: () => void;
}) {
  const items = [
    { key: HOME_ENTRY.key, label: HOME_ENTRY.label, icon: HOME_ENTRY.icon },
    ...props.groups.flatMap((group) =>
      group.items.map((item) => ({
        key: item.key,
        label: item.label,
        icon: item.icon,
        group: group.label,
        ...(item.module
          ? { count: props.countFor(item.module), tone: props.toneFor(item.module) }
          : {}),
      })),
    ),
  ];
  const footer = (
    <Stack gap={2} p="sm">
      <UnstyledButton onClick={props.onProfile} aria-label="Profile">
        <Group gap="xs" wrap="nowrap">
          <IconUser size={18} stroke={1.6} color="var(--mantine-color-gray-6)" />
          <Text fz="sm" c="gray.7" truncate>
            {props.userName}
          </Text>
        </Group>
      </UnstyledButton>
      <UnstyledButton onClick={props.onSignOut}>
        <Group gap="xs" wrap="nowrap">
          <IconLogout size={18} stroke={1.6} color="var(--mantine-color-gray-6)" />
          <Text fz="sm" c="gray.6">
            Sign out
          </Text>
        </Group>
      </UnstyledButton>
    </Stack>
  );

  return (
    <>
      <Box visibleFrom="lg" h="100%">
        <ModuleRail items={items} active={props.active} onSelect={props.onSelect} footer={footer} />
      </Box>
      <Box hiddenFrom="lg" h="100%">
        <ModuleRail items={items} active={props.active} onSelect={props.onSelect} collapsed />
      </Box>
    </>
  );
}

function MoreRow({
  icon,
  label,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      p="sm"
      style={{
        borderRadius: "var(--mantine-radius-md)",
        minHeight: 42,
      }}
    >
      <Group gap="sm">
        <Box c="gray.7" style={{ display: "flex" }}>
          {icon}
        </Box>
        <Text fz="md">{label}</Text>
      </Group>
    </UnstyledButton>
  );
}
